import React from 'react';

export const TermsOfServicePage = () => {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 bg-white text-black dark:bg-gray-800 dark:text-white">
      <div className="bg-brand-surface rounded-xl border border-brand-border p-8">
        <h1 className="text-3xl font-bold text-brand-text-primary mb-6">Terms of Service</h1>
        
        <div className="space-y-6 text-brand-text-primary">
          <section>
            <p className="text-sm text-brand-text-secondary mb-4">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString()}
            </p>
            <p className="mb-4">
              Welcome to TestSeries. These Terms of Service ("Terms") govern your use of our exam preparation platform. By accessing or using our service, you agree to be bound by these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
            <p className="text-brand-text-secondary">
              By creating an account or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Description of Service</h2>
            <p className="text-brand-text-secondary mb-3">
              TestSeries provides online exam preparation services including:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-brand-text-secondary">
              <li>Mock tests and practice questions</li>
              <li>Performance analytics and progress tracking</li>
              <li>Study materials and educational content</li>
              <li>Adaptive learning features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
            <div className="space-y-3 text-brand-text-secondary">
              <p>To use our services, you must:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Be at least 13 years old (or have parental consent)</li>
                <li>Use the service only for lawful purposes</li>
              </ul>
              <p>You are responsible for all activities under your account.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Acceptable Use</h2>
            <p className="mb-3 text-brand-text-secondary">You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 text-brand-text-secondary">
              <li>Share account credentials or allow unauthorized access</li>
              <li>Copy, distribute, or reverse engineer our content</li>
              <li>Use automated tools to access our services</li>
              <li>Interfere with or disrupt our platform</li>
              <li>Upload malicious code or engage in harmful activities</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Intellectual Property</h2>
            <div className="space-y-3 text-brand-text-secondary">
              <p>
                All content on TestSeries, including questions, explanations, software, and design, is owned by us or our licensors and protected by intellectual property laws.
              </p>
              <p>
                You may use our content solely for personal, non-commercial educational purposes. Any other use requires our written permission.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Payment and Subscriptions</h2>
            <div className="space-y-3 text-brand-text-secondary">
              <p>
                Some features may require payment. By purchasing a subscription:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>You agree to pay all applicable fees</li>
                <li>Subscriptions auto-renew unless cancelled</li>
                <li>Refunds are subject to our refund policy</li>
                <li>We may change pricing with notice</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Privacy and Data</h2>
            <p className="text-brand-text-secondary">
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your information. By using our services, you consent to our data practices as described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Disclaimers</h2>
            <div className="space-y-3 text-brand-text-secondary">
              <p>
                Our services are provided "as is" without warranties of any kind. We do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Uninterrupted or error-free service</li>
                <li>Specific exam results or outcomes</li>
                <li>Accuracy of all content or questions</li>
                <li>Compatibility with all devices or browsers</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
            <p className="text-brand-text-secondary">
              To the maximum extent permitted by law, TestSeries shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our services, including but not limited to lost profits, data loss, or exam performance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Termination</h2>
            <p className="text-brand-text-secondary">
              We may suspend or terminate your account at any time for violation of these Terms or for any other reason. You may also terminate your account at any time. Upon termination, your right to use our services ceases immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes to Terms</h2>
            <p className="text-brand-text-secondary">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or platform notification. Continued use after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Governing Law</h2>
            <p className="text-brand-text-secondary">
              These Terms are governed by the laws of India. Any disputes will be resolved in the courts of [PLACEHOLDER: Add your jurisdiction].
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Contact Information</h2>
            <div className="bg-brand-bg p-4 rounded-lg">
              <p className="text-brand-text-secondary mb-2">
                For questions about these Terms, contact us:
              </p>
              <div className="space-y-1 text-brand-text-secondary">
                <p><strong>Email:</strong> nitishkumarsingh1@gmail.com</p>
                <p><strong>Company:</strong> TestSeries</p>
                <p><strong>Address:</strong> Ground Floor, VIM-65, Phase-VII, Rangeswar Nagar, Sailashree Vihar, Patia, Bhubaneswar, Odisha 751021</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};